import type React from 'react'
import { memo, useCallback, useMemo, useState, useEffect } from 'react'
import Sidebar, { type SpecificSidebarProps } from '../Sidebar.tsx'
import { MediaFileFolder } from './MediaFileFolder.tsx'
import MediaFilesListFilters from './MediaFilesListFilters.tsx'
import MediaFileUploadForm from './MediaFileUploadForm.tsx'
import MediaFileBox from './MediaFileBox.tsx'
import { useTranslation } from 'react-i18next'
import { getFolderContent } from '@/features/media-file/domain/use-cases/get-folder-content.ts'
import useUiStore from '../../../shared/store/ui.store.tsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/utils/query-keys.ts'
import type { MediaFolder } from '@/features/media-file/domain/models/media-folder.ts'
import { useShallow } from 'zustand/react/shallow'
import type { MediaFile } from '@/features/media-file/domain/models/media-file.ts'
import Spinner from '@/components/common/Spinner.tsx'
import type { UUID } from '@/features/shared/data/string.types.ts'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface MediaFilesFiltersStore {
  category: UUID | undefined
}

export const useMediaFilesFiltersStore = create(
  persist<MediaFilesFiltersStore>(() => ({ category: undefined }), {
    name: 'media-files-filters',
  })
)

const MediaFilesList: React.FC<SpecificSidebarProps> = ({
  goBack,
  ...props
}) => {
  const { t } = useTranslation()
  const { currentContent, setCurrentContent } = useUiStore(
    useShallow((state) => ({
      currentContent: state.mediaFilesCurrentFolder,
      setCurrentContent: state.setMediaFilesCurrentFolder,
    }))
  )
  const queryClient = useQueryClient()
  const { category } = useMediaFilesFiltersStore()
  const [loadingFolder, setLoadingFolder] = useState<string | null>(null)
  const [showFileUploadForm, setShowFileUploadForm] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [currentFolderId, setCurrentFolderId] = useState<UUID | string>('')

  const { data: folderContent, isLoading } = useQuery({
    queryKey: [QueryKeys.MediaFolderContent, currentContent?.currentFolder.id],
    queryFn: () => getFolderContent(currentContent?.currentFolder.id ?? ''),
  })

  useEffect(() => {
    if (folderContent) {
      setCurrentFolderId(folderContent.currentFolder.id)
    }
  }, [folderContent])

  // Update currentContent when folderContent changes (but keep undefined for root)
  useEffect(() => {
    if (folderContent && folderContent !== currentContent) {
      // Only update store if we're not at the root folder
      // Root folder should keep currentContent as undefined
      if (!folderContent.isRoot) {
        setCurrentContent(folderContent)
      }
    }
  }, [folderContent, currentContent, setCurrentContent])

  const handleBack = useCallback(() => {
    if (showFileUploadForm) {
      setShowFileUploadForm(false)
      return
    }

    if (currentContent?.isRoot || !currentContent?.parentFolder) {
      return
    }

    setLoadingFolder(currentContent.parentFolder.id)
    queryClient
      .fetchQuery({
        queryKey: [
          QueryKeys.MediaFolderContent,
          currentContent.parentFolder.id,
        ],
        queryFn: () => getFolderContent(currentContent.parentFolder?.id),
      })
      .then((data) => {
        setCurrentContent(data)
        setCurrentFolderId(data.currentFolder.id)
        setLoadingFolder(null)
      })
  }, [
    currentContent,
    showFileUploadForm,
    goBack,
    queryClient,
    setCurrentContent,
  ])

  const handleFolderClick = useCallback(
    (folder: MediaFolder) => {
      setLoadingFolder(folder.id)
      queryClient
        .fetchQuery({
          queryKey: [QueryKeys.MediaFolderContent, folder.id],
          queryFn: () => getFolderContent(folder.id),
        })
        .then((data) => {
          setCurrentContent(data)
          setCurrentFolderId(data.currentFolder.id)
          setLoadingFolder(null)
        })
    },
    [queryClient, setCurrentContent]
  )

  const filteredFiles = useMemo((): MediaFile[] => {
    if (!category) return folderContent?.files ?? []
    return (
      folderContent?.files?.filter((file) => file.category?.id === category) ??
      []
    )
  }, [category, folderContent?.files])

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (
        file &&
        (file.type === 'audio/mpeg' ||
          file.type === 'audio/ogg' ||
          file.type === 'audio/wav')
      ) {
        setFile(file)
        setShowFileUploadForm(true)
      }
      event.target.value = ''
    },
    []
  )

  const handleCategoryChange = useCallback((cat: UUID | undefined) => {
    useMediaFilesFiltersStore.setState({ category: cat })
  }, [])

  const handleCloseUploadForm = useCallback(() => {
    setShowFileUploadForm(false)
    setFile(null)
  }, [])

  return (
    <Sidebar
      {...props}
      title={
        showFileUploadForm
          ? 'ls_sidebar_media_file_upload_title'
          : currentContent?.currentFolder.name ||
            t('ls_sidebar_media_files_title')
      }
      searchKey="ls_sidebar_scripts_search"
      {...(!currentContent?.isRoot ? { goBack: handleBack } : {})}
    >
      {showFileUploadForm && file ? (
        currentFolderId ? (
          <MediaFileUploadForm
            file={file}
            folderId={currentFolderId as UUID}
            onClose={handleCloseUploadForm}
          />
        ) : (
          <div className="flex h-full items-center justify-center p-8">
            <Spinner className="h-8 w-8" />
          </div>
        )
      ) : (
        <>
          <div className="sticky top-0 z-20 gap-2 bg-zinc-900 p-4">
            <MediaFilesListFilters
              setCategory={handleCategoryChange}
              handleFileUpload={handleFileUpload}
              initialCategory={category}
            />
          </div>

          <div className="flex flex-col bg-zinc-900 p-0">
            <>
              {currentContent?.parentFolder && (
                <MediaFileFolder
                  isLoading={loadingFolder === currentContent.parentFolder?.id}
                  folder={currentContent.parentFolder}
                  onClick={handleBack}
                  isFolderUp
                />
              )}

              {folderContent?.folders?.map((folder) => (
                <MediaFileFolder
                  isLoading={loadingFolder === folder.id}
                  key={folder.id}
                  folder={folder}
                  onClick={() => handleFolderClick(folder)}
                />
              ))}

              <div className="flex flex-col gap-4 p-4">
                {folderContent?.currentFolder &&
                  filteredFiles.map((file) => (
                    <MediaFileBox
                      key={file.id}
                      file={file}
                      folderId={folderContent?.currentFolder.id}
                    />
                  ))}
              </div>

              {folderContent?.folders?.length === 0 &&
                folderContent?.files?.length === 0 &&
                !isLoading && (
                  <p className="text-center text-gray-500">
                    {t('ls_sidebar_media_files_no_content')}
                  </p>
                )}

              {isLoading && (
                <div className="flex h-20 w-full items-center justify-center">
                  <Spinner className="h-6 w-6" />
                </div>
              )}
            </>
          </div>
        </>
      )}
    </Sidebar>
  )
}

export default memo(MediaFilesList)
